import { action, computed, makeObservable, observable } from 'mobx'

import {
  DeleteFormCategoriesDocument,
  SaveFormCategoriesDocument,
} from '~/client/graph/operations/generated/FormCategories.generated'
import { IFormCategory } from '~/client/graph/types.generated'
import { TagIconType } from '~/client/src/shared/enums/TagIcon'
import { TagType } from '~/client/src/shared/enums/TagType'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Tag from '~/client/src/shared/models/Tag'
import WorkflowCategory from '~/client/src/shared/models/WorkflowCategory'
import GraphExecutorStore from '~/client/src/shared/stores/domain/GraphExecutor.store'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import InitialState from '~/client/src/shared/stores/InitialState'
import { toast } from '~/client/src/shared/utils/toast'
import { UNASSIGNED } from '~/client/src/shared/utils/ZoneLevelLocationConstants'

import Colors from '~/client/src/shared/theme.module.scss'

const createTag = (category: WorkflowCategory): Tag =>
  new Tag(
    category.id,
    category.name,
    category.isDeleted ? Colors.neutral84 : Colors.neutral70,
    TagIconType.FormCategory,
    TagType.FormCategory,
  )

export default class WorkflowCategoriesStore {
  @observable public isDataReceived = false
  @observable private sourceMap: Map<string, WorkflowCategory> = new Map()

  public constructor(
    private readonly eventsStore: EventsStore,
    private readonly graphExecutorStore: GraphExecutorStore,
  ) {
    makeObservable(this)
  }

  @computed
  public get allCategories(): WorkflowCategory[] {
    return Array.from(this.sourceMap.values())
  }

  @computed
  public get activeCategories(): WorkflowCategory[] {
    return this.allCategories
      .filter(c => !c.isDeleted && this.isFlaggedCategoryEnabled(c))
      .sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
      )
  }

  @computed
  public get activeCategoriesAsTags(): Tag[] {
    return this.activeCategories.map(createTag)
  }

  @action.bound
  public receiveList(list: IFormCategory[]) {
    this.clearList()

    list.forEach(dto => this.receiveOne(dto.id, dto))

    this.isDataReceived = true
  }

  @action.bound
  public receiveOne(id: string, dto: IFormCategory) {
    if (dto) {
      const statusChange = WorkflowCategory.fromDto(dto)
      this.sourceMap.set(statusChange.id, statusChange)
    } else {
      this.sourceMap.delete(id)
    }
    this.categoryFlagsCache.delete(id)
  }

  @action.bound
  public async saveCategories(workflowCategories: WorkflowCategory[]) {
    this.state.loading.set(e.SAVE_WORKFLOW_CATEGORIES, true)

    const res = await this.graphExecutorStore.mutate(
      SaveFormCategoriesDocument,
      {
        formCategories: workflowCategories.map(c => c.toDto()),
      },
    )

    this.state.loading.set(e.SAVE_WORKFLOW_CATEGORIES, false)

    if (res?.error) {
      return toast.error(Localization.translator.failedToSave)
    }
  }

  @action.bound
  public async deleteCategories(ids: string[]) {
    this.state.loading.set(e.DELETE_WORKFLOW_CATEGORIES, true)

    const res = await this.graphExecutorStore.mutate(
      DeleteFormCategoriesDocument,
      {
        ids,
      },
    )

    this.state.loading.set(e.DELETE_WORKFLOW_CATEGORIES, false)

    const isDeleted = res?.data?.softDeleteManyFormCategories
    if (res?.error || !isDeleted) {
      return toast.error(Localization.translator.failedToDelete)
    }

    this.markAsDeleted(ids)
  }

  public getById = (
    categoryId: string,
    includeDeleted: boolean = false,
  ): WorkflowCategory => {
    if (!this.sourceMap.has(categoryId)) {
      return
    }
    const instance = this.sourceMap.get(categoryId)
    if (!includeDeleted && instance.isDeleted) {
      return
    }
    return instance
  }

  public getByIdAsTag = (
    categoryId: string,
    includeDeleted: boolean = false,
  ): Tag => {
    const category = this.getById(categoryId, includeDeleted)
    return category ? createTag(category) : null
  }

  public getNameById = (
    categoryId: string,
    includeDeleted: boolean = false,
  ): string => {
    return this.getById(categoryId, includeDeleted)?.name || UNASSIGNED
  }

  public get isDeleting(): boolean {
    return this.state.loading.get(e.DELETE_WORKFLOW_CATEGORIES)
  }

  @action.bound
  public clearList() {
    this.sourceMap.clear()
    this.categoryFlagsCache.clear()
    this.isDataReceived = false
  }

  @action.bound
  private markAsDeleted(ids: string[]) {
    if (!ids?.length) {
      return
    }

    ids.forEach(id => {
      if (!this.sourceMap.has(id)) return

      const category = this.sourceMap.get(id)
      category.isDeleted = true
    })
  }

  /**
   * Checks if a category is enabled based on flagged categories in the application state.
   * Use this when you have the WorkflowCategory object.
   */
  public isFlaggedCategoryEnabled(category: WorkflowCategory): boolean {
    return !!this.state.flaggedFormCategories?.[category.internalName]
  }

  private categoryFlagsCache = observable.map<string, boolean>()
  private lastFlaggedFormCategories: Record<string, boolean> | null = null

  /**
   * Checks if a category is enabled based on flagged categories in the application state.
   * Use this when you only have the category ID.
   * Defaults to true if categoryId is not provided or category is not found.
   * Return false instead to disable unassigned categories.
   */
  public isCategoryFlagEnabled(categoryId: string): boolean {
    const currentFlags = this.state.flaggedFormCategories
    if (currentFlags !== this.lastFlaggedFormCategories) {
      this.categoryFlagsCache.clear()
      this.lastFlaggedFormCategories = currentFlags
    }

    if (!categoryId) {
      return true
    }

    if (this.categoryFlagsCache.has(categoryId)) {
      return this.categoryFlagsCache.get(categoryId) ?? true
    }

    const category = this.getById(categoryId)
    if (!category) {
      return true
    }

    const isEnabled = this.isFlaggedCategoryEnabled(category)
    this.categoryFlagsCache.set(categoryId, isEnabled)
    return isEnabled
  }

  private get state(): InitialState {
    return this.eventsStore.appState
  }
}
